import { Device } from '@/store/Station/constants';
import { DeviceConnectivity, DeviceAction, OnOffEnum } from '@/constants/enums';
import { AlertEntity } from '@/comp/AlertsBar/Alert';
import type {
  DeviceSCCSchema,
  DeviceSchemaAdditionalContext,
} from '@/comp/StationControlCenter/types/device-schema.types';
import type {
  RealtimeMetric,
  RealtimeMetricAdditionalContext,
  SCCDeviceAlert,
} from '@/comp/StationControlCenter/types/universal-device-mapper.types';
import { metricsDetails } from '@/constants';
import type {
  SCCUniversalDevice,
  UniversalDeviceAction,
} from '@/comp/StationControlCenter/types/universal-device.types';
import { DeviceType } from '@/constants/deviceTypeEnum';
import { resolveMetricValue, requiresSpecialHandling } from './MetricValueResolver';

export type { RealtimeMetric, RealtimeMetricAdditionalContext };

export type UniversalSCCDevice = Omit<
  SCCUniversalDevice,
  'deviceType' | 'connectivity' | 'poweredOn' | 'alerts' | 'actions' | 'realtimeMetrics' | 'activity' | 'controlMode'
> & {
  elementIndex: number;
  deviceType: DeviceType;
  connectivity: DeviceConnectivity;
  poweredOn: OnOffEnum | null;
  controlMode?: string;
  activity?: string;
  realtimeMetrics: RealtimeMetric[];
  alerts: SCCDeviceAlert[];
  actions: UniversalDeviceAction[];
};

/**
 * Extended Device interface with additional fields from store
 */
type ExtendedDevice = Device & {
  activity?: string;
  controlMode?: string;
  powerKw?: number;
  estimatedKw?: number;
  flowM3Min?: number;
  estimatedFlowM3Min?: number;
  rpm?: number;
  operatingHours?: number;
  pressureDischarge?: number;
  ratedPowerKw?: number;
  hp?: number;
  activityString?: string;
  dewPoint?: number;
  temperature?: number;
  pressure?: number;
  minPressureThreshold?: number;
  maxPressureThreshold?: number;
  minimumPressureBar?: number;
  maximumPressureBar?: number;
  model?: string;
  serialNumber?: string;
};

type DeviceRealtimeActionsSchema = Pick<DeviceSCCSchema, 'realtimeMetrics' | 'actions'>;

type NormalizedDeviceBase = {
  id: string;
  name: string;
  modelDisplayName?: string;
  model?: string;
  deviceType: DeviceType;
  connectivity: DeviceConnectivity;
  poweredOn: OnOffEnum | null;
  controlMode?: string;
  activity?: string;
  elementIndex: number;
  inMaintenance?: boolean;
};

type NormalizedDevice = {
  extendedDevice: ExtendedDevice;
  base: NormalizedDeviceBase;
  alerts: SCCDeviceAlert[];
};

export const getDeviceValue = (device: ExtendedDevice, field?: string) => {
  if (!field) return undefined;
  if (!Object.prototype.hasOwnProperty.call(device, field)) return undefined;
  return device[field as keyof ExtendedDevice];
};

export const buildAdditionalContext = (
  device: ExtendedDevice,
  additionalContext?: DeviceSchemaAdditionalContext,
): RealtimeMetricAdditionalContext | undefined => {
  if (!additionalContext) return undefined;

  const result: RealtimeMetricAdditionalContext = {};

  if (additionalContext.min) {
    const value = getDeviceValue(device, additionalContext.min) as number | undefined;
    if (value !== undefined) {
      result.min = { metric: additionalContext.min, value };
    }
  }

  if (additionalContext.max) {
    const value = getDeviceValue(device, additionalContext.max) as number | undefined;
    if (value !== undefined) {
      result.max = { metric: additionalContext.max, value };
    }
  }

  if (additionalContext.target !== undefined) {
    if (typeof additionalContext.target === 'number') {
      result.target = additionalContext.target;
    } else {
      const value = getDeviceValue(device, additionalContext.target) as number | undefined;
      if (value !== undefined) {
        result.target = value;
      }
    }
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

export const getMetricUnit = (metric: string): string => {
  return metricsDetails[metric]?.unit ?? '';
};

const getRealtimeMetricsFromSchema = (
  device: ExtendedDevice,
  schema?: DeviceRealtimeActionsSchema,
): RealtimeMetric[] => {
  if (!schema) return [];

  return schema.realtimeMetrics.reduce<RealtimeMetric[]>((metrics, metric) => {
    const defaultValue = getDeviceValue(device, metric.key);

    // Use centralized metric resolver for special handling
    const value = requiresSpecialHandling(metric.key)
      ? resolveMetricValue(device as Parameters<typeof resolveMetricValue>[0], metric.key, defaultValue)
      : defaultValue;

    if (typeof value !== 'number') {
      return metrics;
    }

    const additionalContext = metric.additionalContext
      ? buildAdditionalContext(device, metric.additionalContext)
      : undefined;

    metrics.push({
      key: metric.key,
      value,
      unit: getMetricUnit(metric.key),
      type: metric.type,
      additionalContext,
    });

    return metrics;
  }, []);
};

/**
 * Maps alerts from AlertEntity to DeviceAlert format
 */
const mapAlerts = (alerts: AlertEntity[]): SCCDeviceAlert[] => {
  return alerts.map(alert => ({
    alertRuleId: alert.alertRuleId,
    severity: alert.severity,
    alertType: alert.alertType,
  }));
};

const getDeviceActionsFromSchema = (
  device: ExtendedDevice,
  schema?: DeviceRealtimeActionsSchema,
): UniversalDeviceAction[] => {
  if (!schema?.actions?.length) {
    return [];
  }

  return schema.actions.map(action => {
    if (action === DeviceAction.InMaintenance) {
      return { key: action, value: Boolean(device.inMaintenance) };
    }

    return { key: action };
  });
};

const normalizeDevice = (device: Device, alerts: AlertEntity[] = []): NormalizedDevice => {
  const extendedDevice = device as ExtendedDevice;

  return {
    extendedDevice,
    base: {
      id: device.deviceId,
      name: device.name,
      modelDisplayName: device.modelDisplayName,
      model: extendedDevice.model,
      deviceType: device.deviceType,
      connectivity: device.connectivity,
      poweredOn: device.poweredOn as OnOffEnum,
      controlMode: extendedDevice.controlMode,
      activity: extendedDevice.activity,
      elementIndex: device.elementIndex ?? 0,
      inMaintenance: device.inMaintenance,
    },
    alerts: mapAlerts(alerts),
  };
};

const normalizedToScc = (normalized: NormalizedDevice, deviceSchema?: DeviceSCCSchema): UniversalSCCDevice => {
  const realtimeMetrics = getRealtimeMetricsFromSchema(normalized.extendedDevice, deviceSchema);
  const actions = getDeviceActionsFromSchema(normalized.extendedDevice, deviceSchema);

  return {
    ...normalized.base,
    realtimeMetrics,
    alerts: normalized.alerts,
    actions,
    messages: [],
  };
};

export const mapDeviceToUniversalSCC = (
  device: Device,
  alerts: AlertEntity[] = [],
  deviceSchema?: DeviceSCCSchema,
): UniversalSCCDevice => {
  return normalizedToScc(normalizeDevice(device, alerts), deviceSchema);
};

export const mapDevicesToUniversalSCC = (
  devices: Device[],
  getAlertsForDevice: (deviceId: string) => AlertEntity[] = () => [],
  deviceSchemas: DeviceSCCSchema[] = [],
): UniversalSCCDevice[] => {
  return devices.map(device => {
    const schema = deviceSchemas?.find(s => s.deviceType === device.deviceType);
    return mapDeviceToUniversalSCC(device, getAlertsForDevice(device.deviceId) || [], schema);
  });
};
